<template>
  <div class="l-page l-page--message">
    <!-- サイドバーエリア -->
    <page-sidebar />
    <!-- /サイドバーエリア -->
    <main class="l-main">
      <div class="l-main-head">
        <div class="c-title u-w600">
          <div class="c-title__text c-title__text--blue u-fs34">リストアップロード</div>
        </div>
      </div>
      <div class="l-main-content">
        <form class="unique-key-upload" @submit.prevent="onSubmit">
          <fieldset class="unique-key-upload__fields" :disabled="isUploading">
            <div class="unique-key-upload__field">
              <label class="unique-key-upload__label" for="uniqueKeyListName">リスト名</label>
              <div class="c-input u-w600">
                <input
                  id="uniqueKeyListName"
                  v-model="listName"
                  class="c-input__item"
                  type="text"
                  name="list_name"
                  maxlength="300"
                  placeholder="例：9月 有料会員"
                />
              </div>
              <p class="unique-key-upload__hint">300文字以内</p>
              <p v-if="fieldErrors.list_name" class="c-error__text">{{ fieldErrors.list_name }}</p>
            </div>

            <div class="unique-key-upload__field">
              <span class="unique-key-upload__label">CSV ファイル</span>
              <div class="c-upload" style="width: auto;">
                <label class="c-upload__label" style="padding: 0; width: auto;">
                  <div class="c-upload__data" style="display: inline; padding: 0; width: auto;">
                    <input
                      id="uniqueKeyListFile"
                      name="file"
                      type="file"
                      accept=".csv,text/csv,text/plain"
                      @change="onFileChange"
                    />
                    <span class="c-upload__btn">ファイルを選択</span>
                    <span class="c-upload__filename" style="width: auto;">{{ fileLabel }}</span>
                  </div>
                </label>
              </div>
              <ul class="unique-key-upload__notes">
                <li>UTF-8 で、1列目にユニークキーを入れてください（2列目以降は使いません）</li>
                <li>1行目が unique_key の場合は見出しとして読み飛ばします</li>
                <li>最大 100,000 行・10MB までです</li>
                <li>Excel で保存すると、先頭の 0 が消えることがあります（例：00123 → 123）。キーが一致しなくなるので注意してください</li>
              </ul>
              <p v-if="fieldErrors.file" class="c-error__text">{{ fieldErrors.file }}</p>
            </div>
          </fieldset>

          <div v-if="rowErrorMessage" class="unique-key-upload__row-errors">
            <p class="c-error__text">{{ rowErrorMessage }}</p>
            <ul>
              <li v-for="rowError in rowErrors" :key="rowError.line">
                {{ rowError.line.toLocaleString() }} 行目：{{ rowError.reason }}
              </li>
            </ul>
          </div>
          <p v-if="isUploading" class="unique-key-upload__busy">取り込んでいます。終わるまでこの画面を閉じないでください。</p>

          <button
            type="submit"
            class="c-btn c-btn--blue u-w320 u-h50 u-fs20"
            :class="{ 'is-loading': isUploading }"
            :disabled="!canSubmit"
          >
            <p class="c-btn__text">{{ isUploading ? 'アップロード中…' : 'アップロードする' }}</p>
          </button>
        </form>
      </div>
    </main>

    <error-modal :is-active="errorMessage !== ''" :message="errorMessage" :on-close-click="onCloseErrorModal" />
  </div>
</template>

<script>
import axios from 'axios';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default {
  props: {
    csrfToken: String,
  },
  data() {
    return {
      listName: '',
      file: null,
      isUploading: false,
      fieldErrors: {},
      rowErrorMessage: '',
      rowErrors: [],
      errorMessage: '',
    };
  },
  computed: {
    fileLabel: function () {
      if (!this.file) {
        return '選択されていません';
      }
      const size = this.file.size < 1024 * 1024
        ? `${Math.max(1, Math.round(this.file.size / 1024))}KB`
        : `${(this.file.size / 1024 / 1024).toFixed(1)}MB`;
      return `${this.file.name}（${size}）`;
    },
    canSubmit: function () {
      return !this.isUploading && this.listName.trim() !== '' && this.file !== null && !this.fieldErrors.file;
    },
  },
  methods: {
    onFileChange: function (event) {
      this.file = event.target.files[0] || null;
      this.clearErrors();
      if (this.file && this.file.size > MAX_FILE_SIZE) {
        this.fieldErrors = { file: 'ファイルが 10MB を超えています。分けてアップロードしてください' };
      }
    },
    clearErrors: function () {
      this.fieldErrors = {};
      this.rowErrorMessage = '';
      this.rowErrors = [];
    },
    onSubmit: function () {
      if (!this.canSubmit) {
        return;
      }
      this.clearErrors();
      this.isUploading = true;

      const formData = new FormData();
      formData.append('list_name', this.listName.trim());
      formData.append('file', this.file);

      axios
        .post('/api/unique-key-list/save', formData)
        .then((res) => {
          if (res.data.result === 'OK') {
            location.href = '/coins-admin/unique-key-list/review?list_id=' + res.data.list_id;
            return;
          }
          this.isUploading = false;
          const errors = res.data.errors || {};
          if (errors.rows) {
            this.rowErrorMessage = res.data.message;
            this.rowErrors = errors.rows;
          } else if (errors.file || errors.list_name) {
            this.fieldErrors = { file: errors.file, list_name: errors.list_name };
          } else {
            this.errorMessage = res.data.message;
          }
        })
        .catch((error) => {
          this.handleHttpError(error);
          this.isUploading = false;
          this.errorMessage = 'アップロードに失敗しました。再度お試しください。';
        });
    },
    onCloseErrorModal: function () {
      this.errorMessage = '';
    },
  },
};
</script>

<style scoped>
.unique-key-upload {
  display: grid;
  gap: 28px;
  max-width: 760px;
}
.unique-key-upload__fields {
  border: 0;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 28px;
}
.unique-key-upload__field {
  display: grid;
  gap: 8px;
}
.unique-key-upload__label {
  font-weight: bold;
}
.unique-key-upload__hint {
  color: #666;
  font-size: 12px;
}
.unique-key-upload__notes {
  color: #666;
  font-size: 13px;
  line-height: 1.8;
  list-style: disc;
  padding-left: 1.4em;
}
.unique-key-upload__row-errors {
  border-left: 3px solid #e2574c;
  background: #fdf1f0;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.8;
}
.unique-key-upload__busy {
  color: #666;
  font-size: 13px;
}
</style>
